import { FindHint } from '../components/document-viewer/types';
import { DocumentVerifyKind } from '../components/document-viewer/locate/find-hints';
import { CustomFixtureField } from './custom-fixtures';
import { SwProfile, formatDate, formatMoneyPlain } from './star-wars-profile';

export type FixtureTemplateId =
    | 'thermal_receipt'
    | 'hotel_folio'
    | 'line_item'
    | 'paystub'
    | 'tax'
    | 'w2'
    | 'social_security'
    | 'affidavit';

export type FixtureFormat = 'png' | 'jpeg' | 'pdf-image' | 'text-pdf';
export type FixtureQuality = 'clean' | 'dirty-light' | 'dirty-heavy' | 'dirty-severe' | 'dirty-hostile';
export type FixtureOrientation = 0 | 90 | 180 | 270;

export interface FixtureRecipe {
    seed: number;
    profile: SwProfile;
    templateId: FixtureTemplateId;
    format: FixtureFormat;
    quality: FixtureQuality;
    orientation: FixtureOrientation;
    filename: string;
    label: string;
    expenseTypeName: string;
    amount: number;
    dateLabel: string;
    fields: CustomFixtureField[];
    verifyKind?: DocumentVerifyKind;
    family: 'lwr' | 'expense' | 'other';
}

export const FIXTURE_TEMPLATES: Array<{ id: FixtureTemplateId; label: string; family: 'lwr' | 'expense' | 'other' }> = [
    { id: 'thermal_receipt', label: 'Thermal receipt', family: 'expense' },
    { id: 'hotel_folio', label: 'Hotel folio', family: 'expense' },
    { id: 'line_item', label: 'Line item', family: 'expense' },
    { id: 'paystub', label: 'Pay stub', family: 'lwr' },
    { id: 'tax', label: 'Tax document', family: 'lwr' },
    { id: 'w2', label: 'W-2', family: 'lwr' },
    { id: 'social_security', label: 'Social Security', family: 'lwr' },
    { id: 'affidavit', label: 'Affidavit', family: 'lwr' }
];

export const FIXTURE_FORMATS: Array<{ id: FixtureFormat; label: string }> = [
    { id: 'png', label: 'PNG' },
    { id: 'jpeg', label: 'JPEG' },
    { id: 'pdf-image', label: 'Image PDF' },
    { id: 'text-pdf', label: 'Text PDF' }
];

export const FIXTURE_QUALITIES: Array<{ id: FixtureQuality; label: string }> = [
    { id: 'clean', label: 'Clean' },
    { id: 'dirty-light', label: 'Dirty light' },
    { id: 'dirty-heavy', label: 'Dirty heavy' },
    { id: 'dirty-severe', label: 'Dirty severe' },
    { id: 'dirty-hostile', label: 'Dirty hostile' }
];

export const FIXTURE_ORIENTATIONS: Array<{ id: FixtureOrientation; label: string }> = [
    { id: 0, label: '0°' },
    { id: 90, label: '90°' },
    { id: 180, label: '180°' },
    { id: 270, label: '270°' }
];

export const SHELF_SOFT_CAP = 24;
export const SHELF_HARD_CAP = 40;

function ssnLast4(p: SwProfile): string {
    return (p.ssnDecrypted || '').replace(/\D/g, '').slice(-4);
}

function ssnFull(p: SwProfile): string {
    const d = (p.ssnDecrypted || '').replace(/\D/g, '');
    if (d.length < 9) {
        return d;
    }
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

function extFor(format: FixtureFormat): string {
    if (format === 'png') {
        return 'png';
    }
    if (format === 'jpeg') {
        return 'jpg';
    }
    return 'pdf';
}

function familyFor(templateId: FixtureTemplateId): 'lwr' | 'expense' | 'other' {
    const row = FIXTURE_TEMPLATES.filter(t => t.id === templateId)[0];
    return row ? row.family : 'other';
}

function expenseTypeFor(templateId: FixtureTemplateId): string {
    if (templateId === 'hotel_folio') {
        return 'Lodging';
    }
    if (familyFor(templateId) === 'lwr') {
        return 'Income';
    }
    if (templateId === 'thermal_receipt') {
        return 'Meals';
    }
    return 'Other';
}

function verifyKindFor(templateId: FixtureTemplateId): DocumentVerifyKind | undefined {
    if (templateId === 'paystub') {
        return 'paystub';
    }
    if (templateId === 'tax') {
        return 'tax';
    }
    if (templateId === 'w2') {
        return 'w2';
    }
    if (templateId === 'social_security') {
        return 'social_security';
    }
    if (templateId === 'affidavit') {
        return 'affidavit';
    }
    if (familyFor(templateId) === 'expense') {
        return 'expense';
    }
    return undefined;
}

function labelFor(templateId: FixtureTemplateId): string {
    const row = FIXTURE_TEMPLATES.filter(t => t.id === templateId)[0];
    return row ? ('SW ' + row.label) : templateId;
}

function fieldsFor(templateId: FixtureTemplateId, p: SwProfile): CustomFixtureField[] {
    const amount = formatMoneyPlain(p.receiptAmount || 0);
    const date = formatDate(p.receiptDate || '');
    const line2 = Math.round(((p.receiptAmount || 0) * 0.42) * 100) / 100;
    const line1 = Math.round(((p.receiptAmount || 0) - line2 - 2.99) * 100) / 100;
    const subtotal = Math.round((line1 + line2 + 2.99) * 100) / 100;
    const tax = Math.round((subtotal * 0.0825) * 100) / 100;
    const total = Math.round((subtotal + tax) * 100) / 100;

    if (templateId === 'thermal_receipt') {
        return [
            { id: 'merchant', label: 'Merchant', value: p.merchant },
            { id: 'guest', label: 'Guest', value: p.fullName },
            { id: 'subtotal', label: 'Subtotal', value: formatMoneyPlain(subtotal), detail: '$' + formatMoneyPlain(subtotal) },
            { id: 'total', label: 'Total', value: formatMoneyPlain(total), detail: '$' + formatMoneyPlain(total) },
            { id: 'date', label: 'Date', value: date }
        ];
    }
    if (templateId === 'hotel_folio') {
        return [
            { id: 'vendor', label: 'Vendor', value: p.vendor },
            { id: 'guest', label: 'Guest', value: p.fullName },
            { id: 'amount', label: 'Amount', value: amount, detail: '$' + amount },
            { id: 'date', label: 'Date', value: date }
        ];
    }
    if (templateId === 'paystub') {
        return [
            { id: 'employee', label: 'Employee', value: p.fullName },
            { id: 'ssn', label: 'SSN last4', value: ssnLast4(p) },
            { id: 'stubStart', label: 'Stub start', value: formatDate(p.incomePayStubStartDate || '') },
            { id: 'stubEnd', label: 'Stub end', value: formatDate(p.incomePayStubEndDate || '') },
            { id: 'stubGross', label: 'Stub gross', value: formatMoneyPlain(p.incomePayStubGross || 0) }
        ];
    }
    if (templateId === 'tax' || templateId === 'w2') {
        return [
            { id: 'employee', label: 'Employee', value: p.fullName },
            { id: 'ssn', label: 'SSN', value: ssnFull(p) },
            { id: 'year', label: 'Tax year', value: String(p.incomeIrsYear || '') },
            { id: 'irsGross', label: 'IRS gross', value: formatMoneyPlain(p.incomeIrsYearGross || 0) }
        ];
    }
    if (templateId === 'social_security') {
        return [
            { id: 'beneficiary', label: 'Beneficiary', value: p.fullName },
            { id: 'ssn', label: 'SSN', value: ssnFull(p) },
            { id: 'year', label: 'Benefit year', value: String(p.incomeIrsYear || '') },
            { id: 'amount', label: 'Benefit amount', value: formatMoneyPlain(p.incomeIrsYearGross || 0) }
        ];
    }
    if (templateId === 'affidavit') {
        return [
            { id: 'name', label: 'Name', value: p.fullName },
            { id: 'ssn', label: 'SSN last4', value: ssnLast4(p) },
            { id: 'year', label: 'Tax year', value: String(p.incomeIrsYear || '') },
            { id: 'irsGross', label: 'IRS gross', value: formatMoneyPlain(p.incomeIrsYearGross || 0) }
        ];
    }
    return [
        { id: 'merchant', label: 'Merchant', value: p.merchant },
        { id: 'soldTo', label: 'Sold to', value: p.fullName },
        { id: 'amount', label: 'Amount', value: amount, detail: '$' + amount },
        { id: 'date', label: 'Date', value: date }
    ];
}

export function buildFixtureRecipe(
    profile: SwProfile,
    templateId: FixtureTemplateId,
    format: FixtureFormat,
    quality: FixtureQuality,
    orientation: FixtureOrientation = 0
): FixtureRecipe {
    const fields = fieldsFor(templateId, profile);
    const amountField = fields.filter(f => f.id === 'total' || f.id === 'amount' || f.id === 'stubGross' || f.id === 'irsGross')[0];
    const amount = amountField ? parseFloat(amountField.value) : (profile.receiptAmount || 0);
    const qualityPart = format === 'text-pdf' ? 'clean' : quality;
    const orient = format === 'text-pdf' ? 0 : orientation;
    const orientPart = orient ? `-rot${orient}` : '';
    return {
        seed: profile.seed,
        profile,
        templateId,
        format,
        quality: format === 'text-pdf' ? 'clean' : quality,
        orientation: orient,
        filename: `sw-${profile.seed}-${templateId}-${format}-${qualityPart}${orientPart}.${extFor(format)}`,
        label: labelFor(templateId),
        expenseTypeName: expenseTypeFor(templateId),
        amount: isNaN(amount) ? (profile.receiptAmount || 0) : amount,
        dateLabel: formatDate(profile.receiptDate || ''),
        fields,
        verifyKind: verifyKindFor(templateId),
        family: familyFor(templateId)
    };
}

export function buildRecipeFindHints(fields: CustomFixtureField[]): FindHint[] {
    return fields
        .filter(f => !!(f.value || '').trim())
        .map(f => ({
            id: 'factory-' + f.id,
            label: f.label,
            value: f.value,
            detail: f.detail || f.value
        }));
}

export function textPdfFilename(seed: number, templateId: string, variant: string): string {
    return `sw-${seed}-${templateId}-${variant}.pdf`;
}

export function receiptMoneyParts(p: SwProfile): {
    line1: number;
    line2: number;
    line3: number;
    subtotal: number;
    tax: number;
    total: number;
} {
    const line2 = Math.round(((p.receiptAmount || 0) * 0.42) * 100) / 100;
    const line3 = 2.99;
    const line1 = Math.round(((p.receiptAmount || 0) - line2 - line3) * 100) / 100;
    const subtotal = Math.round((line1 + line2 + line3) * 100) / 100;
    const tax = Math.round((subtotal * 0.0825) * 100) / 100;
    const total = Math.round((subtotal + tax) * 100) / 100;
    return { line1, line2, line3, subtotal, tax, total };
}

export function labTemplateIdFor(templateId: FixtureTemplateId): string {
    if (templateId === 'affidavit') {
        return 'self_employed';
    }
    if (templateId === 'social_security') {
        return 'social_security';
    }
    return templateId;
}

export function jpegQualityFor(quality: FixtureQuality): number {
    if (quality === 'clean') {
        return 0.92;
    }
    if (quality === 'dirty-light') {
        return 0.65;
    }
    if (quality === 'dirty-heavy') {
        return 0.45;
    }
    if (quality === 'dirty-severe') {
        return 0.34;
    }
    return 0.22;
}
